import { useAsyncData, useNuxtApp, useRouter } from '#imports'
import { type Ref, computed } from 'vue'

import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { toBcp47Locale } from '@backmarket/utils/string/toBcp47Locale'

import { PRODUCT_ROUTES } from '~/scopes/product-core/route-names'

import type { RatingValue, TableData } from '../../models/productComparison'
import {
  type ProductSpecification,
  buildComparisonTable,
} from '../../utils/comparisonHelper'

import translations from './useEnrichResultsPage.translations'
import type {
  ComparisonData,
  EnrichedData,
  Product,
  ReviewRating,
} from './useEnrichResultsPage.types'

export function useEnrichResultsPage(
  comparisonData: Ref<ComparisonData | null | undefined>,
  comparisonPending: Ref<boolean>,
) {
  const $httpFetch = useHttpFetch()
  const nuxtApp = useNuxtApp()
  const i18n = useI18n()
  const router = useRouter()

  function productPageUrl(slug: string) {
    return router.resolve({
      name: PRODUCT_ROUTES.MASTER_PRODUCT_PAGE,
      params: { locale: toBcp47Locale(i18n.currentLocale), slug },
      hash: '#reviews',
    }).href
  }

  async function fetchReviews(productId: string): Promise<ReviewRating | null> {
    try {
      return (await $httpFetch(
        '/reviews/v1/products/:productId/reviews-summary',
        { pathParams: { productId } },
      )) as ReviewRating
    } catch {
      return null
    }
  }

  async function fetchTechnicalSpecs(
    productId: string,
  ): Promise<ProductSpecification[]> {
    const response = await $httpFetch(
      '/product-page/products/:productId/technical_specifications',
      { pathParams: { productId } },
    )

    return (response.specifications ?? []) as ProductSpecification[]
  }

  async function fetchEnrichedData(): Promise<EnrichedData> {
    if (!comparisonData.value?.products) {
      return { products: [] }
    }

    const compareProducts = comparisonData.value.products
    const productIds = compareProducts.map((p) => p.productId)

    const [reviews, technicalSpecifications] = await Promise.all([
      Promise.all(
        productIds.map((id) => nuxtApp.runWithContext(() => fetchReviews(id))),
      ),
      Promise.all(
        productIds.map((id) =>
          nuxtApp.runWithContext(() => fetchTechnicalSpecs(id)),
        ),
      ),
    ])

    return {
      products: compareProducts.map(
        (product, i): Product => ({
          product,
          review: reviews[i] ?? undefined,
          specifications: technicalSpecifications[i] ?? [],
        }),
      ),
    }
  }

  const { data: enrichedData, pending: enrichmentPending } =
    useAsyncData<EnrichedData>(
      () => {
        const ids = comparisonData.value?.products.map((p) => p.productId)

        return `models-comparison-enrichment-${ids?.join('-')}`
      },
      fetchEnrichedData,
      { default: (): EnrichedData => ({ products: [] }) },
    )

  const products = computed(() => comparisonData.value?.products ?? [])
  const pending = computed(
    () => comparisonPending.value || enrichmentPending.value,
  )

  const ratings = computed<RatingValue[]>(() => {
    const enrichedProducts = enrichedData.value?.products
    if (!enrichedProducts) return []

    const hasAnyReview = enrichedProducts.some((p) => p.review != null)
    if (!hasAnyReview) return []

    return enrichedProducts.map((p) => ({
      averageRate: p.review?.averageRate ?? null,
      count: p.review?.count ?? null,
      url: productPageUrl(p.product.slug),
    }))
  })

  const tableData = computed<TableData[]>(() => {
    const enrichedProducts = enrichedData.value?.products
    if (!enrichedProducts) return []

    return buildComparisonTable({
      textBlocks: comparisonData.value?.textBlocks ?? [],
      specs: enrichedProducts.map((p) => p.specifications),
      scores: enrichedProducts.map((p) =>
        Object.fromEntries(
          p.product.scores.map((s) => [s.id, String(s.value)]),
        ),
      ),
      labels: {
        introduction: i18n(translations.introduction),
        conclusion: i18n(translations.conclusion),
        scores: i18n(translations.scores),
        durability: i18n(translations.durability),
        performance: i18n(translations.performance),
        screenQuality: i18n(translations.screenQuality),
        soundQuality: i18n(translations.soundQuality),
        multimedia: i18n(translations.multimedia),
        camera: i18n(translations.camera),
        miscelaneous: i18n(translations.miscelaneous),
      },
    })
  })

  return { products, ratings, tableData, pending }
}
